import CountdownTimer from "./CountdownTimer";
import { ArrowRight, Terminal, CloudLightning, GraduationCap, Cloud } from "lucide-react";

export default function HeroSection() {
  return (
    <section
      data-testid="hero-section"
      className="relative pt-24 pb-16 sm:pt-32 sm:pb-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto grid-bg"
    >
      <div className="text-left max-w-3xl mb-12">
        <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 bg-[#16161F] border border-[#27272A] rounded-md">
          <Terminal size={14} className="text-[#22D3EE]" />
          <span className="text-xs font-mono font-medium text-[#22D3EE] uppercase tracking-wider">
            4 Courses &middot; AWS, Azure & OCI Cloud Mastery &middot; 2026
          </span>
        </div>

        <h1 className="font-['Space_Grotesk'] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none font-bold text-[#F4F4F5] mb-6">
          Master Cloud Infrastructure{" "}
          <span className="text-[#E32636]">AWS, Azure & OCI</span>
        </h1>

        <p className="text-base sm:text-lg text-[#A1A1AA] leading-relaxed mb-4 max-w-2xl">
          Oracle DBA <ArrowRight size={14} className="inline mx-1" /> OCI Architect / Cloud DBA <ArrowRight size={14} className="inline mx-1" /> AIML Data Architect.
          Learn from 3+ decades of goal-oriented IT professional experience.
        </p>
        <p className="text-sm text-[#71717A] leading-relaxed mb-8 max-w-2xl">
          Four comprehensive certification courses to future-proof your career in cloud architecture, infrastructure, and database management.
        </p>

        <div className="flex flex-wrap gap-3">
          <a href="#aws-course" data-testid="hero-aws-button" className="inline-flex items-center justify-center gap-2 bg-[#FF9900] hover:bg-[#FFB84D] text-[#0A0A0F] font-semibold px-5 py-2.5 rounded-md transition-all duration-200 text-sm">
            <CloudLightning size={16} /> AWS Infra
          </a>
          <a href="#combined-course" data-testid="hero-combined-button" className="inline-flex items-center justify-center gap-2 bg-[#6366F1] hover:bg-[#818CF8] text-white font-semibold px-5 py-2.5 rounded-md transition-all duration-200 text-sm">
            AWS+DB Architect
          </a>
          <a href="#azure-course" data-testid="hero-azure-button" className="inline-flex items-center justify-center gap-2 bg-[#0078D4] hover:bg-[#1A8FE0] text-white font-semibold px-5 py-2.5 rounded-md transition-all duration-200 text-sm">
            <Cloud size={16} /> Azure AZ-305
          </a>
          <a href="#oci-course" data-testid="hero-oci-button" className="inline-flex items-center justify-center gap-2 bg-[#E32636] hover:bg-[#FF2D3B] text-white font-semibold px-5 py-2.5 rounded-md transition-all duration-200 text-sm glow-red">
            OCI Course
          </a>
          <a href="#enroll" data-testid="hero-enroll-button" className="inline-flex items-center justify-center border border-[#27272A] hover:border-[#22D3EE] hover:text-[#22D3EE] text-[#A1A1AA] font-semibold px-5 py-2.5 rounded-md transition-all duration-200 text-sm">
            Enroll Now
          </a>
        </div>
      </div>

      {/* Four course cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* AWS Infra Card */}
        <a href="#aws-course" className="block p-5 bg-[#16161F] border border-[#FF9900]/30 rounded-md hover:border-[#FF9900]/60 transition-all duration-300">
          <div className="flex items-center gap-2 mb-2">
            <CloudLightning size={16} className="text-[#FF9900]" />
            <span className="font-mono text-[10px] text-[#FF9900] uppercase tracking-wider">Course 01</span>
          </div>
          <h3 className="font-['Space_Grotesk'] text-sm font-bold text-[#F4F4F5] mb-1">AWS Cloud Infrastructure</h3>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#71717A]">
            <span>65 Hrs</span>
            <span>8 Modules</span>
            <span>15-Jun-2026</span>
          </div>
        </a>

        {/* Combined Card */}
        <a href="#combined-course" className="block p-5 bg-[#16161F] border border-[#6366F1]/30 rounded-md hover:border-[#6366F1]/60 transition-all duration-300">
          <div className="flex items-center gap-2 mb-2">
            <GraduationCap size={16} className="text-[#6366F1]" />
            <span className="font-mono text-[10px] text-[#6366F1] uppercase tracking-wider">Combined</span>
          </div>
          <h3 className="font-['Space_Grotesk'] text-sm font-bold text-[#F4F4F5] mb-1">AWS Cloud & DB Architect</h3>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#71717A]">
            <span>100 Hrs</span>
            <span>21 Modules</span>
            <span>Ongoing</span>
          </div>
        </a>

        {/* Azure Card */}
        <a href="#azure-course" className="block p-5 bg-[#16161F] border border-[#0078D4]/30 rounded-md hover:border-[#0078D4]/60 transition-all duration-300">
          <div className="flex items-center gap-2 mb-2">
            <Cloud size={16} className="text-[#0078D4]" />
            <span className="font-mono text-[10px] text-[#0078D4] uppercase tracking-wider">New Batch</span>
          </div>
          <h3 className="font-['Space_Grotesk'] text-sm font-bold text-[#F4F4F5] mb-1">Azure AZ-305 for OCI</h3>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#71717A]">
            <span>60 Hrs</span>
            <span>AZ-305</span>
            <span>Demo 10-Nov-2026</span>
          </div>
        </a>

        {/* OCI Card */}
        <a href="#oci-course" className="block p-5 bg-[#16161F] border border-[#E32636]/30 rounded-md hover:border-[#E32636]/60 transition-all duration-300">
          <div className="flex items-center gap-2 mb-2">
            <Terminal size={16} className="text-[#E32636]" />
            <span className="font-mono text-[10px] text-[#E32636] uppercase tracking-wider">Course 03</span>
          </div>
          <h3 className="font-['Space_Grotesk'] text-sm font-bold text-[#F4F4F5] mb-1">OCI Mastery for Oracle DBA</h3>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#71717A]">
            <span>60-65 Hrs</span>
            <span>10 Modules</span>
            <span>Demo 30-May-2026</span>
          </div>
        </a>
      </div>
    </section>
  );
}
